/**
 * ProcureAI Enterprise Deterministic Mock Data Seeder
 * Seed: PROCUREAI_DEMO_2026
 * 
 * Provides PRNG implementation (Mulberry32) and deterministic generators
 * for high-fidelity enterprise mock datasets across all procurement domain entities.
 */

export const SEED_KEY = 'PROCUREAI_DEMO_2026';

/**
 * Creates a deterministic Mulberry32 Pseudo-Random Number Generator (PRNG)
 * initialized with a string seed hash.
 */
export function createPRNG(seedStr: string = SEED_KEY): () => number {
  let h = 2166136261;
  for (let i = 0; i < seedStr.length; i++) {
    h = Math.imul(h ^ seedStr.charCodeAt(i), 16777619);
  }

  let state = h >>> 0;

  return function nextFloat(): number {
    let t = (state += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rng = createPRNG(SEED_KEY);

/**
 * Returns a random integer in range [min, max] deterministically.
 */
export function randomInt(min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min;
}

/**
 * Returns a random element from an array deterministically.
 */
export function randomChoice<T>(array: T[]): T {
  return array[randomInt(0, array.length - 1)]!;
}

/**
 * Returns a subset of elements from an array deterministically.
 */
export function randomSample<T>(array: T[], count: number): T[] {
  const shuffled = [...array].sort(() => rng() - 0.5);
  return shuffled.slice(0, count);
}

/**
 * Generates an ISO 8601 timestamp deterministically offset from base date.
 */
export function randomDateISO(startDaysAgo: number = 180, endDaysAgo: number = 0): string {
  const now = new Date('2026-09-01T12:00:00Z').getTime();
  const dayMs = 86400 * 1000;
  const targetMs = now - randomInt(endDaysAgo * dayMs, startDaysAgo * dayMs);
  return new Date(targetMs).toISOString();
}

/**
 * Enterprise naming generators for seed datasets
 */
const VENDOR_PREFIXES = ['Apex', 'Nexus', 'Vertex', 'Synergy', 'Global', 'Quantum', 'Titan', 'Vanguard', 'Omni', 'Integrity', 'Pinnacle', 'Starlight'];
const VENDOR_SUFFIXES = ['Solutions', 'Technologies', 'Industries', 'Systems', 'Enterprise', 'Logistics', 'Components', 'Networks', 'Dynamics', 'Hardware'];

export function generateEnterpriseVendorName(index: number): string {
  const prefix = VENDOR_PREFIXES[index % VENDOR_PREFIXES.length]!;
  const suffix = VENDOR_SUFFIXES[Math.floor(index / VENDOR_PREFIXES.length) % VENDOR_SUFFIXES.length]!;
  return `${prefix} ${suffix} ${index > 50 ? 'Group' : 'Corp'}`;
}
